import { CheckCircle, Mail } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '../components/ui';
import { organizationInvitationService } from '../services';

const AcceptOrganizationInvitation: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requiresRegistrationFields, setRequiresRegistrationFields] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const getErrorDetail = (error: unknown): string => {
    const detail = (error as { data?: { detail?: unknown } })?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (error instanceof Error) return error.message;
    return t('organizationInvitations.acceptGenericError');
  };

  const handleAccept = async () => {
    if (!token) {
      toast.error(t('organizationInvitations.tokenMissing'));
      return;
    }

    setIsSubmitting(true);
    try {
      await organizationInvitationService.acceptInvitation({
        token,
        first_name: requiresRegistrationFields ? firstName : undefined,
        last_name: requiresRegistrationFields ? lastName : undefined,
        password: requiresRegistrationFields ? password : undefined,
      });
      setAccepted(true);
      toast.success(t('organizationInvitations.acceptSuccess'));
    } catch (error) {
      const detail = getErrorDetail(error);
      if (
        detail.toLowerCase().includes('first_name') ||
        detail.toLowerCase().includes('last_name') ||
        detail.toLowerCase().includes('password') ||
        detail.toLowerCase().includes('exige')
      ) {
        setRequiresRegistrationFields(true);
      }
      toast.error(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-graphite-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>{t('organizationInvitations.acceptTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!token ? (
            <p className="text-sm text-red-600 dark:text-red-400">
              {t('organizationInvitations.tokenMissing')}
            </p>
          ) : accepted ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                <CheckCircle className="h-5 w-5" />
                <p className="text-sm">{t('organizationInvitations.acceptSuccess')}</p>
              </div>
              <Link to="/login">
                <Button variant="primary" className="w-full">
                  {t('organizationInvitations.goToLogin')}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {requiresRegistrationFields
                  ? t('organizationInvitations.completeRegistration')
                  : t('organizationInvitations.acceptDescription')}
              </p>

              {requiresRegistrationFields && (
                <div className="space-y-3">
                  <Input
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    placeholder={t('auth.firstName')}
                  />
                  <Input
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    placeholder={t('auth.lastName')}
                  />
                  <Input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder={t('auth.password')}
                    leftIcon={<Mail className="h-4 w-4" />}
                  />
                </div>
              )}

              <Button
                variant="primary"
                className="w-full"
                onClick={handleAccept}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('common.saving') : t('organizationInvitations.acceptButton')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptOrganizationInvitation;
